import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';

export type OAuthProviderName = 'google' | 'microsoft';

/** Where a provider's credentials resolve from. `null` means unconfigured. */
export type OAuthSource = 'settings' | 'env' | null;

export interface OAuthIntegration {
  provider: OAuthProviderName;
  label: string;
  /** The client ID saved in Settings. Null when only the env is configured —
   *  the API deliberately does not echo the env value back. */
  client_id: string | null;
  /** Whether a secret is stored. The secret itself is never returned. */
  has_secret: boolean;
  source: OAuthSource;
  /** The exact string that must be registered in the provider console. */
  redirect_uri: string;
  updated_at: number | null;
  updated_by_name: string | null;
}

export type ProbeResult = 'accepted' | 'rejected' | 'unknown';

export function useOAuthIntegrations() {
  return useQuery({
    queryKey: ['integrations-oauth'],
    queryFn: ({ signal }) =>
      api.get<{ providers: OAuthIntegration[] }>('/api/integrations/oauth', signal),
  });
}

export interface SaveOAuthInput {
  provider: OAuthProviderName;
  client_id: string;
  /** Omit to keep the stored secret — the admin cannot read it back to retype. */
  client_secret?: string;
}

export function useSaveOAuthIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ provider, ...body }: SaveOAuthInput) =>
      api.put<{ provider: string; configured: boolean }>(
        `/api/integrations/oauth/${provider}`,
        body,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations-oauth'] });
      // The Calendar page gates its Connect button on this.
      void qc.invalidateQueries({ queryKey: ['calendar-sync-providers'] });
    },
  });
}

export function useRemoveOAuthIntegration() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (provider: OAuthProviderName) =>
      api.delete<void>(`/api/integrations/oauth/${provider}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['integrations-oauth'] });
      void qc.invalidateQueries({ queryKey: ['calendar-sync-providers'] });
    },
  });
}

export function useTestOAuthIntegration() {
  return useMutation({
    mutationFn: (provider: OAuthProviderName) =>
      api.post<{ provider: string; result: ProbeResult; message: string }>(
        `/api/integrations/oauth/${provider}/test`,
      ),
  });
}
